import React, { useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

const AmazonCallback = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useAuthUser(); // Hook auf der obersten Ebene aufrufen
    const authHeader = useAuthHeader(); // Hook auf der obersten Ebene aufrufen

    useEffect(() => {
        // Extract query params from URL (e.g., code, state, error)
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');
        const state = params.get('state');

        console.log('Amazon Callback received:', { code, error, state });

        if (error) {
            // Handle error (e.g., show message, redirect)
            window.opener?.postMessage({ type: 'amazonAuthFailed', error: 'amazon_auth_failed' }, '*');
            window.close();
            return;
        }

        // Retrieve state and original URL from local storage
        const amazonAuthStateStr = localStorage.getItem('amazonAuthState');

        if (!amazonAuthStateStr) {
            console.error('No amazonAuthState found in localStorage');
            window.opener?.postMessage({ type: 'amazonAuthFailed', error: 'no_state_found' }, '*');
            window.close();
            return;
        }

        let amazonAuthState;
        try {
            amazonAuthState = JSON.parse(amazonAuthStateStr);
        } catch (e) {
            console.error('Failed to parse amazonAuthState:', e);
            window.opener?.postMessage({ type: 'amazonAuthFailed', error: 'invalid_state' }, '*');
            window.close();
            return;
        }

        if (!amazonAuthState || amazonAuthState.state !== state) {
            console.error('CSRF detected or state mismatch', { expected: amazonAuthState?.state, received: state });
            window.opener?.postMessage({ type: 'amazonAuthFailed', error: 'csrf_mismatch' }, '*');
            window.close();
            return;
        }

        const { originalUrl, flowConfigKey } = amazonAuthState;

        if (code) {
            // Funktion um die Daten zu senden
            const sendCredentials = async () => {
                try {
                    // Verwende die Hooks, die bereits auf der obersten Ebene aufgerufen wurden
                    if (!auth || !authHeader) {
                        console.error('Authentication not available');
                        window.opener?.postMessage({
                            type: 'amazonAuthFailed',
                            error: 'auth_not_available'
                        }, '*');
                        window.close();
                        return;
                    }

                    // API-Call Options
                    const options = {
                        headers: {
                            Authorization: authHeader,
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    };

                    const data = {
                        tenant_credential_secret: code,
                        tenant_credential_tag: flowConfigKey + '.amazon',
                        tenant_credential_username: amazonAuthState.clientId || 'amazon_sp_api'
                    };

                    console.log('Sending credentials:', data);

                    // Dynamische URL basierend auf der Umgebung
                    const baseURL = process.env.REACT_APP_ENV === 'development'
                        ? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
                        : `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

                    const response = await axios.post(`${baseURL}/tenants/${auth.tenant_id}/credentials`, data, options);

                    console.log('Backend response:', response.data);

                    if (response.data) {
                        window.opener?.postMessage({
                            type: 'amazonAuthSuccess',
                            originalUrl: originalUrl,
                            data: response.data
                        }, '*');
                    } else {
                        window.opener?.postMessage({
                            type: 'amazonAuthFailed',
                            error: 'backend_failed',
                            details: response.data
                        }, '*');
                    }
                } catch (error) {
                    console.error('Error sending credentials:', error);
                    window.opener?.postMessage({
                        type: 'amazonAuthFailed',
                        error: 'api_error',
                        details: error.response?.data || error.message
                    }, '*');
                }

                window.close();
            };

            sendCredentials();
        } else {
            console.error('No authorization code received');
            window.opener?.postMessage({ type: 'amazonAuthFailed', error: 'no_code' }, '*');
            window.close();
        }

        // Remove state from local storage after use
        localStorage.removeItem('amazonAuthState');
    }, [location, navigate, auth, authHeader]); // Dependencies hinzufügen

    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Authenticating with Amazon...</h2>
            <p>Please wait while we process your authentication.</p>
        </div>
    );
};

export default AmazonCallback;